import { DocumentItemModel } from '../../models'

export const orderDocumentItems = <TItem extends DocumentItemModel>(items: TItem[]): TItem[] => {
    return [...items].sort((left, right) => left.index - right.index)
}

const withContiguousIndexes = <TItem extends DocumentItemModel>(items: TItem[]): TItem[] => {
    return items.map((item, index) => ({ ...item, index: index + 1 }))
}

/**
 * Moves a single item to a new position, renumbering every item so the indexes stay contiguous
 * and 1-based.
 *
 * `fromIndex` and `toIndex` are positions within `items` exactly as passed. Callers take those
 * positions from the rendered list, and the document keeps its items in rendered order (see
 * `orderDocumentItems`, applied whenever items arrive from the server), so nothing is sorted
 * here — sorting first would silently reinterpret the caller's positions against a different
 * array. A position outside the array leaves `items` untouched.
 */
export const reorderDocumentItems = <TItem extends DocumentItemModel>(items: TItem[], fromIndex: number, toIndex: number): TItem[] => {
    if (fromIndex < 0 || fromIndex >= items.length || toIndex < 0 || toIndex >= items.length) {
        return items
    }

    const reorderedItems = [...items]
    const [movedItem] = reorderedItems.splice(fromIndex, 1)

    reorderedItems.splice(toIndex, 0, movedItem)

    return withContiguousIndexes(reorderedItems)
}
